import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";
import { AdminCard, PageTitle, ROLE_LABELS } from "@/pages/admin/AdminLayout";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const cls = "w-full h-11 px-4 rounded-xl border border-sand bg-white text-sm text-ocean focus:outline-none focus:border-sea";
const PERMS = {
  owner: "Tutto: impostazioni, utenti, registro attività, eliminazioni definitive, GDPR.",
  manager: "Tour, pacchetti, diario, recensioni, prenotazioni, clienti, export. Non gestisce utenti e impostazioni.",
  staff: "Prenotazioni e richieste (lettura e aggiornamento stato), clienti in lettura.",
};
const empty = { email: "", name: "", role: "staff", password: "", active: true };

export default function AdminUsers() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | user
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/users").then((r) => setList(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setF(empty); setEditing("new"); };
  const openEdit = (u) => { setF({ email: u.email, name: u.name || "", role: u.role || "owner", password: "", active: u.active !== false }); setEditing(u); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, password: f.password || null };
      if (editing === "new") await adminApi.post("/admin/users", payload);
      else await adminApi.patch(`/admin/users/${editing.id}`, payload);
      toast.success(editing === "new" ? "Utente creato" : "Utente aggiornato");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare l'accesso di ${u.email}?`)) return;
    try { await adminApi.delete(`/admin/users/${u.id}`); toast.success("Utente eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-users">
      <PageTitle title="Utenti e permessi" sub="Accessi al back-office con ruoli. I permessi sono applicati dal server."
        right={<button onClick={openNew} className="inline-flex items-center gap-2 px-5 py-3 bg-ocean text-ivory text-sm font-medium rounded-xl btn-lift" data-testid="admin-user-new"><Plus size={16} />Nuovo utente</button>} />
      <AdminCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-ivory/70 text-[11px] uppercase tracking-[0.15em] text-ocean/55">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Utente</th>
                <th className="text-left px-5 py-3 font-medium">Ruolo</th>
                <th className="text-left px-5 py-3 font-medium">Stato</th>
                <th className="text-left px-5 py-3 font-medium">Ultimo accesso</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-sand/60">
              {loading && <tr><td colSpan={5} className="px-5 py-10 text-center text-ocean/45">Caricamento...</td></tr>}
              {list.map((u) => (
                <tr key={u.id || u.email} className="hover:bg-sand/20" data-testid={`admin-user-row-${u.email}`}>
                  <td className="px-5 py-4"><p className="text-ocean font-medium">{u.name || "—"}</p><p className="text-xs text-ocean/50">{u.email}</p></td>
                  <td className="px-5 py-4 text-ocean/80">{ROLE_LABELS[u.role] || u.role || "Titolare"}</td>
                  <td className="px-5 py-4"><span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium border ${u.active === false ? "bg-sand/60 text-ocean/60 border-sand" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>{u.active === false ? "Disattivato" : "Attivo"}</span></td>
                  <td className="px-5 py-4 text-xs text-ocean/60 whitespace-nowrap">{u.last_login_at ? new Date(u.last_login_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2 text-ocean/60">
                      <button onClick={() => openEdit(u)} aria-label="Modifica" className="p-2 hover:text-ocean" data-testid={`admin-user-edit-${u.email}`}><Pencil size={16} /></button>
                      <button onClick={() => remove(u)} aria-label="Elimina" className="p-2 text-rose-700" data-testid={`admin-user-delete-${u.email}`}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AdminCard>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent className="w-full sm:max-w-md bg-white overflow-y-auto" data-testid="admin-user-editor">
          <SheetHeader>
            <SheetTitle className="font-serif text-2xl text-ocean">{editing === "new" ? "Nuovo utente" : "Modifica utente"}</SheetTitle>
            <SheetDescription>{PERMS[f.role]}</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-5">
            <div><label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">Email</label><input type="email" className={cls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-user-email" /></div>
            <div><label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">Nome</label><input className={cls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-user-name" /></div>
            <div>
              <label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">Ruolo</label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger className="h-11 rounded-xl border-sand bg-white" data-testid="admin-user-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="block text-[11px] tracking-[0.18em] uppercase text-ocean/55 mb-2">{editing === "new" ? "Password (min. 8 caratteri)" : "Nuova password (lascia vuoto per non cambiarla)"}</label><input type="password" className={cls} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-user-password" /></div>
            <label className="flex items-center gap-3 text-sm text-ocean"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="admin-user-active" />Accesso attivo</label>
          </div>
          <div className="mt-8 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="px-5 py-3 rounded-xl border border-sand text-sm text-ocean">Annulla</button>
            <button onClick={save} disabled={saving} className="px-6 py-3 rounded-xl bg-ocean text-ivory text-sm font-medium disabled:opacity-60 btn-lift" data-testid="admin-user-save">{saving ? "Salvataggio..." : "Salva"}</button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
